import type { NodeV2 } from '@vedalib/editor'
import type { EditorElement, ElementValue } from '@vedalib/editor/lib/elements'
import React, { useCallback, useMemo } from 'react'

import { ELEMENTS_CONFIG } from 'components/Editor/EditorElements/elements.config'
import type { FCEditorActions } from 'components/Editor/EditorElements/elements.types'
import ControlsGroup from 'components/Editor/cource/layout/controls/ControlsGroup'
import { setElement, updateNodeState } from 'services/Store/Project/actions'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getElementState, getNodeState } from 'services/Store/Project/selectors'
import type { Block, ElementState, IBlockMode } from 'services/Store/Project/types'

interface IElementControlsProps {
  element: EditorElement
  mode: IBlockMode
  block: Block
  node: NodeV2
  onChange: (value: ElementValue) => void
}

const Element: React.FC<IElementControlsProps> = ({ element, block, node, mode, onChange }) => {
  const dispatch = useProjectDispatch()
  const elementState = useProjectContext(getElementState, element.id)
  const nodeState = useProjectContext(getNodeState, node.id)

  const customMode = useMemo(
    () => ({ ...mode, editorMode: nodeState?.fill ? AppMode.fill : mode.editorMode }),
    [mode, nodeState?.fill],
  )
  const handleSetState = useCallback(
    (value: ElementState) => dispatch(setElement({ id: element.id, value })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [element.id],
  )

  const setAppMode = useCallback(
    (value: AppMode) => {
      dispatch(updateNodeState({ id: node.id, state: { fill: value === AppMode.fill } }))
    },
    [dispatch, node.id],
  )

  const Actions = ELEMENTS_CONFIG[element.type]?.EditorActions as FCEditorActions | null
  const actionsContent = Actions ? (
    <Actions
      block={block}
      customMode={customMode}
      element={element}
      mode={mode}
      onChange={onChange}
      setAppMode={setAppMode}
      setState={handleSetState}
      state={elementState}
    />
  ) : null

  if (!Actions) {
    return null
  }

  return <ControlsGroup>{actionsContent}</ControlsGroup>
}

export default React.memo(Element)
